import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Loader2, CheckCircle2, Send, FileSpreadsheet, FileText, Workflow, Inbox, Eye, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";

export default function Onboarding() {
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const locked = status === "submitted";
  const [view, setView] = useState("form");
  const [subs, setSubs] = useState([]);
  const [detailDoc, setDetailDoc] = useState(null);
  const [jobBusy, setJobBusy] = useState("");
  const navigate = useNavigate();

  const loadSubs = () => api.get("/onboarding/submissions").then(({ data }) => setSubs(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { if (isAdmin && view === "responses") loadSubs(); }, [view, isAdmin]);
  const openDetail = async (email) => {
    try { const { data } = await api.get(`/onboarding/submissions/${encodeURIComponent(email)}`); setDetailDoc(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createJob = async (email) => {
    setJobBusy(email);
    try { const { data } = await api.post("/onboarding/jobs/from-response", { email, sla_hours: 24 }); toast.success(`Orchestration job created with ${data.tasks.length} task(s).`); navigate("/orchestration"); }
    catch (e) { toast.error(apiErr(e)); } finally { setJobBusy(""); }
  };

  useEffect(() => {
    Promise.all([api.get("/onboarding/schema"), api.get("/onboarding/response")])
      .then(([s, r]) => { setSchema(s.data); setAnswers(r.data.answers || {}); setStatus(r.data.status || "draft"); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const setField = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/onboarding/response", { answers }); toast.success("Draft saved."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!window.confirm("Submit your responses? You won't be able to edit afterwards.")) return;
    setSubmitting(true);
    try { await api.post("/onboarding/response/submit", { answers }); setStatus("submitted"); toast.success("Response submitted. Thank you."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const { data } = await api.get(`/onboarding/export/${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `onboarding_responses.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(""); }
  };

  const answered = schema ? schema.sections.flatMap((s) => s.questions).filter((q) => (answers[q.id] || "").trim()).length : 0;
  const total = schema ? schema.sections.flatMap((s) => s.questions).length : 0;

  if (loading) return (<div className="min-h-screen"><TopBar /><div className="flex justify-center py-24 text-[var(--burgundy)]"><Loader2 className="w-6 h-6 animate-spin" /></div></div>);

  return (
    <div className="min-h-screen pb-24" data-testid="onboarding-page">
      <TopBar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: "var(--burgundy)" }}>
              <Workflow className="w-7 h-7" /> Onboarding Workflow Discovery
            </h1>
            <p className="text-neutral-500 mt-1 text-sm max-w-2xl">{schema.intro}</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button data-testid="onb-export-csv" onClick={() => exportFile("csv")} disabled={!!exporting} className="btn btn-outline">
                {exporting === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} CSV
              </button>
              <button data-testid="onb-export-pdf" onClick={() => exportFile("pdf")} disabled={!!exporting} className="btn btn-outline">
                {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
              </button>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="flex items-center gap-1 mt-6 border-b border-[hsl(var(--border))]">
            <button data-testid="onb-tab-form" onClick={() => setView("form")} className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ${view === "form" ? "border-[var(--burgundy)] text-[var(--burgundy)]" : "border-transparent text-neutral-500 hover:text-neutral-700"}`}>My form</button>
            <button data-testid="onb-tab-responses" onClick={() => setView("responses")} className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors inline-flex items-center gap-1.5 ${view === "responses" ? "border-[var(--burgundy)] text-[var(--burgundy)]" : "border-transparent text-neutral-500 hover:text-neutral-700"}`}><Inbox className="w-4 h-4" /> Responses</button>
          </div>
        )}

        {isAdmin && view === "responses" ? (
          <div className="mt-6" data-testid="onb-responses-view">
            {subs.length === 0 ? (
              <div className="text-center text-neutral-400 py-16 card-surface" data-testid="onb-responses-empty">No responses yet.</div>
            ) : (
              <div className="space-y-2">
                {subs.map((s) => (
                  <div key={s.email} className="card-surface p-4 flex items-center justify-between gap-3" data-testid={`onb-sub-${s.email}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-800 truncate">{s.email}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        <span className={`px-2 py-0.5 rounded-full ${s.status === "submitted" ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-600"}`}>{s.status}</span>
                        <span className="ml-2">{s.answered}/{s.total} answered</span>
                        {s.submitted_at && <span className="ml-2">· {new Date(s.submitted_at).toLocaleString()}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`onb-view-${s.email}`} onClick={() => openDetail(s.email)} className="btn btn-outline !py-1.5 !px-3 text-xs"><Eye className="w-3.5 h-3.5" /> View</button>
                      {s.status === "submitted" && (
                        <button data-testid={`onb-createjob-${s.email}`} onClick={() => createJob(s.email)} disabled={!!jobBusy} className="btn btn-primary !py-1.5 !px-3 text-xs whitespace-nowrap">
                          {jobBusy === s.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Workflow className="w-3.5 h-3.5" />} Create job
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <>
        {locked ? (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm inline-flex items-center gap-2" data-testid="onb-submitted-banner">
            <CheckCircle2 className="w-4 h-4" /> Submitted — your responses are read-only.
          </div>
        ) : (
          <div className="mt-4 text-xs text-neutral-500">{answered} of {total} questions answered · drafts save for later.</div>
        )}

        <div className="mt-6 space-y-6">
          {schema.sections.map((s) => (
            <div key={s.id} className="card-surface p-5" data-testid={`onb-section-${s.id}`}>
              <h2 className="text-base font-semibold text-[var(--burgundy)] border-l-4 border-[var(--burgundy)] pl-3">{s.title}</h2>
              <p className="text-xs text-neutral-500 mt-1 mb-3 pl-3">{s.desc}</p>
              <div className="space-y-4">
                {s.questions.map((q, i) => (
                  <div key={q.id}>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      <span className="text-[var(--burgundy)] font-semibold mr-1">{i + 1}.</span>{q.label}
                    </label>
                    <textarea
                      data-testid={`onb-input-${q.id}`}
                      value={answers[q.id] || ""}
                      onChange={(e) => setField(q.id, e.target.value)}
                      disabled={locked}
                      rows={2}
                      className="field-input w-full disabled:bg-neutral-50 disabled:text-neutral-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!locked && (
          <div className="sticky bottom-4 mt-6 flex justify-end gap-2 bg-[var(--parchment)]/80 backdrop-blur rounded-lg p-2">
            <button data-testid="onb-save-btn" onClick={save} disabled={saving || submitting} className="btn btn-outline">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
            </button>
            <button data-testid="onb-submit-btn" onClick={submit} disabled={saving || submitting} className="btn btn-primary">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {detailDoc && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={(e) => { if (e.target === e.currentTarget) setDetailDoc(null); }} data-testid="onb-detail">
          <div className="w-full max-w-2xl bg-[var(--parchment)] h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="brand-band text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div className="min-w-0"><p className="text-xs text-amber-200 uppercase tracking-widest">Onboarding response</p><p className="font-semibold truncate">{detailDoc.email}</p></div>
              <button onClick={() => setDetailDoc(null)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              {schema.sections.map((s) => (
                <div key={s.id}>
                  <h3 className="text-sm font-semibold text-[var(--burgundy)] border-l-4 border-[var(--burgundy)] pl-2">{s.title}</h3>
                  <div className="mt-2 space-y-2">
                    {s.questions.map((q) => (
                      <div key={q.id}>
                        <p className="text-xs text-neutral-400">{q.label}</p>
                        <p className="text-sm text-neutral-800 whitespace-pre-wrap">{(detailDoc.answers || {})[q.id]?.trim() || "—"}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
